/**
 * @file Dashboard shell with Material UI drawer navigation.
 */
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { StorylineHelpBrand } from "@/components/StorylineHelpBrand";
import { DashboardAccountMenu } from "@/components/dashboard/DashboardAccountMenu";
import { useAuth } from "@/lib/auth-context";
import {
  isAuth0CallbackInProgress,
  replacePathIfChanged,
} from "@/lib/safe-navigation";
import { useEffect, useMemo, useRef, useState } from "react";

const DRAWER_WIDTH = 268;

const navItems = [
  { href: "/dashboard", label: "Overview", icon: DashboardOutlinedIcon },
  { href: "/dashboard/guides", label: "Guides", icon: MenuBookOutlinedIcon },
  { href: "/dashboard/chapters", label: "Chapters", icon: LibraryBooksOutlinedIcon },
  { href: "/dashboard/pages", label: "All pages", icon: ArticleOutlinedIcon },
  {
    href: "/dashboard/approvals",
    label: "Pending approvals",
    icon: PendingOutlinedIcon,
    roles: ["admin", "reviewer"] as const,
  },
];

export function DashboardLayout() {
  const {
    user,
    loading,
    ssoEnabled,
    ssoInitializing,
    completingSignIn,
    exchangeError,
    clearExchangeError,
    loginWithSso,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const ssoKickoffRef = useRef(false);

  const nav = useMemo(() => {
    const roles = user?.roles ?? [];
    return navItems.filter((item) => {
      if (!item.roles?.length) {
        return true;
      }
      if (!roles.length) {
        return false;
      }
      return item.roles.some((r) => roles.includes(r));
    });
  }, [user?.roles]);

  useEffect(() => {
    const unauthenticated =
      !user &&
      !loading &&
      !ssoInitializing &&
      !completingSignIn &&
      !exchangeError;
    if (!unauthenticated) {
      ssoKickoffRef.current = false;
      return;
    }
    if (isAuth0CallbackInProgress()) {
      return;
    }
    if (ssoEnabled) {
      if (ssoKickoffRef.current) {
        return;
      }
      ssoKickoffRef.current = true;
      loginWithSso();
      return;
    }
    replacePathIfChanged(navigate, "/login");
  }, [
    user,
    loading,
    ssoEnabled,
    ssoInitializing,
    completingSignIn,
    exchangeError,
    navigate,
    loginWithSso,
  ]);

  if (exchangeError) {
    return (
      <Box
        display="flex"
        minHeight="100vh"
        alignItems="center"
        justifyContent="center"
        px={2}
      >
        <Alert
          severity="error"
          sx={{ maxWidth: 480 }}
          action={
            <Box display="flex" flexDirection="column" gap={1} alignItems="stretch">
              <Button size="small" variant="outlined" onClick={() => {
                clearExchangeError();
                loginWithSso();
              }}
              >
                Try again
              </Button>
              <Button size="small" color="inherit" onClick={() => logout()}>
                Sign out
              </Button>
            </Box>
          }
        >
          {exchangeError}
        </Alert>
      </Box>
    );
  }

  if (loading || ssoInitializing || completingSignIn || !user) {
    return (
      <Box display="flex" minHeight="100vh" alignItems="center" justifyContent="center" color="text.secondary">
        Loading…
      </Box>
    );
  }

  const drawer = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Toolbar sx={{ px: 2 }}>
        <StorylineHelpBrand variant="h6" iconSize={28} />
      </Toolbar>
      <Divider />
      <List sx={{ px: 1, py: 1, flex: 1 }}>
        {nav.map((item) => {
          const active =
            item.href === "/dashboard"
              ? pathname === "/dashboard"
              : pathname === item.href || pathname.startsWith(`${item.href}/`);
          const Icon = item.icon;
          return (
            <ListItemButton
              key={item.href}
              component={Link}
      to={item.href}
              selected={active}
              onClick={() => setMobileOpen(false)}
              sx={{ borderRadius: 2, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: active ? "primary.main" : "text.secondary" }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: active ? 600 : 500 }} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          color: "text.primary",
        }}
      >
        <Toolbar>
          {!isSmUp && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 2 }} aria-label="Open menu">
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="subtitle1" fontWeight={600} noWrap component="div" sx={{ flexGrow: 1 }}>
            Documentation
          </Typography>
          <DashboardAccountMenu />
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: DRAWER_WIDTH, borderRight: 1, borderColor: "divider" },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: "100vh",
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}
